// Defensive mapper from /auth/me raw payload to CurrentUserView.
// Returns null for malformed input so the caller can fall back to a
// signed-out state instead of crashing the page.

import type { CurrentUserView, UserOptionView } from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toStringArray(value: unknown): ReadonlyArray<string> {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

function toOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function toOptionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function toTimeouts(value: unknown): CurrentUserView['timeouts'] {
  if (!isRecord(value)) return undefined;
  return {
    uiStreamTimeoutSeconds: toOptionalNumber(value.ui_stream_timeout_seconds),
    backendStreamTimeoutSeconds: toOptionalNumber(value.backend_stream_timeout_seconds),
    llmIdleTimeoutSeconds: toOptionalNumber(value.llm_idle_timeout_seconds),
  };
}

function toUploadLimits(value: unknown): CurrentUserView['uploadLimits'] {
  if (!isRecord(value)) return undefined;
  return {
    allowedExtensions: toStringArray(value.allowed_extensions),
    maxCount: toOptionalNumber(value.max_count),
    maxBytes: toOptionalNumber(value.max_bytes),
  };
}

export function toCurrentUserView(raw: unknown): CurrentUserView | null {
  if (!isRecord(raw)) return null;

  // /auth/me wraps the user under `user` on some endpoints and returns
  // a flat object on others. Accept both rather than coupling to one.
  const userPart = isRecord(raw.user) ? raw.user : raw;
  const id = toOptionalString(userPart.user_id) ?? toOptionalString(userPart.id);
  if (!id) return null;

  return {
    id,
    email: toOptionalString(userPart.email) ?? '',
    name:
      toOptionalString(userPart.name) ??
      toOptionalString(userPart.display_name) ??
      '',
    internalUserId: toOptionalString(raw.internal_user_id) ?? null,
    avatarUrl: toOptionalString(userPart.avatar_url),
    groups: toStringArray(userPart.groups),
    allowedAgents: toStringArray(raw.allowed_agents),
    ownedAgents: toStringArray(raw.owned_agents),
    isAdmin: raw.is_admin === true,
    isRealAdmin: raw.is_real_admin === true || raw.is_admin === true,
    skillsHubEnabled: raw.skills_hub_enabled === true,
    pendingIamGroups: toStringArray(raw.pending_iam_groups),
    timeouts: toTimeouts(raw.timeouts),
    uploadLimits: toUploadLimits(raw.upload_limits),
    sessionExpiresAt: toOptionalNumber(raw.session_expires_at),
    authSessionId: toOptionalString(raw.auth_session_id),
    authSessionStartedAt: toOptionalString(raw.auth_session_started_at),
  };
}

export function toUserOptionView(raw: unknown): UserOptionView | null {
  if (!isRecord(raw)) return null;
  const id = toOptionalString(raw.user_id) ?? toOptionalString(raw.id);
  if (!id) return null;
  return {
    id,
    email: toOptionalString(raw.email) ?? '',
    displayName:
      toOptionalString(raw.display_name) ?? toOptionalString(raw.email) ?? id,
  };
}

export function toUserOptionList(raw: unknown): ReadonlyArray<UserOptionView> {
  if (!isRecord(raw)) return [];
  const rows = Array.isArray(raw.users) ? raw.users : [];
  return rows
    .map(toUserOptionView)
    .filter((u): u is UserOptionView => u !== null);
}
